/**
 * Same line walk as domain `src/skills/skill-md.util.ts`.
 * SKILL.md: opening `---`, a `name:` line, a `description:` line, a closing `---`, then a body.
 * Unknown frontmatter keys are ignored when name, description, and a body are present.
 */
export type ParsedSkillMarkdown = {
  name: string;
  description: string;
  prompt: string;
};

export type SkillMarkdownParseResult =
  | { ok: true; skill: ParsedSkillMarkdown }
  | { ok: false; error: string };

export function parseSkillMarkdown(raw: string): SkillMarkdownParseResult {
  const text = normalizeNewlines(String(raw ?? ''));
  const lines = text.split('\n');
  if (lines.length === 0 || lines[0].trim() !== '---') {
    return { ok: false, error: 'File must start with ---.' };
  }

  let name = '';
  let description = '';
  let sawName = false;
  let sawDescription = false;
  let currentKey = '';
  let closed = false;
  let index = 1;

  while (index < lines.length) {
    const line = lines[index];
    if (line.trim() === '---') {
      closed = true;
      index += 1;
      break;
    }
    const continued = line.startsWith(' ') || line.startsWith('\t');
    if (continued && currentKey !== '') {
      const extra = line.trim();
      if (extra !== '') {
        if (currentKey === 'name') {
          name = joinFrontmatter(name, extra);
        } else if (currentKey === 'description') {
          description = joinFrontmatter(description, extra);
        }
      }
      index += 1;
      continue;
    }
    const colon = line.indexOf(':');
    if (colon < 1) {
      return { ok: false, error: 'Frontmatter lines must look like name: value.' };
    }
    const key = line.slice(0, colon).trim().toLowerCase();
    let value = unquoteFrontmatter(line.slice(colon + 1).trim());
    if (value === '|' || value === '>' || value === '|-' || value === '>-' || value === '|+' || value === '>+') {
      value = '';
    }
    currentKey = key;
    if (key === 'name' && !sawName) {
      name = value;
      sawName = true;
    } else if (key === 'description' && !sawDescription) {
      description = value;
      sawDescription = true;
    }
    index += 1;
  }

  if (!closed) {
    return { ok: false, error: 'Frontmatter must close with ---.' };
  }
  if (!sawName || name.trim() === '') {
    return { ok: false, error: 'Frontmatter needs a name.' };
  }
  if (!sawDescription || description.trim() === '') {
    return { ok: false, error: 'Frontmatter needs a description.' };
  }
  const prompt = lines.slice(index).join('\n').trim();
  if (prompt === '') {
    return { ok: false, error: 'The instructions after the frontmatter are empty.' };
  }
  return {
    ok: true,
    skill: {
      name: name.trim(),
      description: description.trim(),
      prompt,
    },
  };
}

function normalizeNewlines(raw: string): string {
  let out = '';
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '\r') {
      out += '\n';
      if (raw[i + 1] === '\n') {
        i += 1;
      }
      continue;
    }
    out += ch;
  }
  return out;
}

function joinFrontmatter(current: string, extra: string): string {
  if (current === '') {
    return extra;
  }
  return `${current} ${extra}`;
}

function unquoteFrontmatter(value: string): string {
  if (value.length < 2) {
    return value;
  }
  const open = value[0];
  const close = value[value.length - 1];
  if ((open === '"' && close === '"') || (open === "'" && close === "'")) {
    return value.slice(1, -1).trim();
  }
  return value;
}
